'use client';
import { useSyncExternalStore } from 'react';

interface CookieOptions {
   maxAge?: number;
   path?: string;
   sameSite?: 'Strict' | 'Lax' | 'None';
   domain?: string;
   secure?: boolean;
}

/**
 * Hook to persist state in a browser cookie.
 *
 * With `parse` and `fallback`, returns a strongly typed value that is always defined.
 * Without them, returns undefined while loading, then the raw string or null.
 */
export function useCookieState<T extends string>(
   name: string,
   options: CookieOptions & {
      parse: (value: string) => T | null;
      fallback: T;
   }
): [T, (value: T) => void];

export function useCookieState(
   name: string,
   options?: CookieOptions
): [string | null | undefined, (value: string) => void];

export function useCookieState(
   name: string,
   options?: CookieOptions & {
      parse?: (value: string) => string | null;
      fallback?: string;
   }
): [string | null | undefined, (value: string) => void] {
   const rawValue = useSyncExternalStore<string | null | undefined>(
      subscribeToCookieChanges,
      () => readCookie(name),
      serverSnapshot
   );

   const setCookieValue = (newValue: string) => {
      writeCookie(name, newValue, options);
   };

   const { parse, fallback } = options ?? {};
   let value: string | null | undefined;
   if (rawValue != null && parse) {
      value = parse(rawValue) ?? fallback ?? null;
   } else if (rawValue == null) {
      value = fallback ?? rawValue;
   } else {
      value = rawValue;
   }

   return [value, setCookieValue];
}

function serverSnapshot(): string | null | undefined {
   return;
}

const cookieListeners = new Set<() => void>();

function subscribeToCookieChanges(callback: () => void) {
   cookieListeners.add(callback);
   return () => {
      cookieListeners.delete(callback);
   };
}

/** Fire-and-forget write for callers that never read the cookie back. */
export function writeCookie(name: string, value: string, options?: CookieOptions): void {
   const parts = [`${name}=${encodeURIComponent(value)}`];
   if (options?.path) parts.push(`path=${options.path}`);
   if (options?.maxAge != null) parts.push(`max-age=${options.maxAge}`);
   if (options?.domain) parts.push(`domain=${options.domain}`);
   if (options?.secure) parts.push('Secure');
   if (options?.sameSite) parts.push(`SameSite=${options.sameSite}`);
   document.cookie = parts.join(';');
   notifyCookieChange();
}

export function notifyCookieChange() {
   for (const listener of cookieListeners) {
      listener();
   }
}

export function readCookie(name: string): string | null {
   const prefix = `${name}=`;
   const match = document.cookie.split('; ').find(row => row.startsWith(prefix));
   return match ? decodeCookieValue(match.slice(prefix.length)) : null;
}

function decodeCookieValue(value: string): string {
   try {
      return decodeURIComponent(value);
   } catch {
      return value;
   }
}
